import React, {createContext, PropsWithChildren, useContext, useEffect} from "react"
import {useAccessToken, useIsAuthenticated} from "./auth"
import useAsyncResultIdle from "../hooks/useAsyncResultIdle"
import {getOrgBankingSetup} from "../resources/orgBankingSetupType"
import Loading from "../components/Loading/Loading"
import ChooseBuildPath from "../pages/ChooseBuildPath/ChooseBuildPath"
import {Err} from "../components/Err/Err"
import {isSandboxOrLocalEnv} from "../utilities/environment"
import {claimsContext} from "./claims"
import {assertIsDefined} from "../utilities/assert"

export type OrgBankingSetupState = "NotSet" | "ReadyToLaunchBanking" | "Custom"

type OrgBankingSetupContextType = {
	setup?: OrgBankingSetupState
	fetch: () => void
}

const orgBankingSetupContext = createContext<OrgBankingSetupContextType | undefined>(undefined)

export function OrgBankingSetupProvider({children}: PropsWithChildren<{}>) {
	const isAuthenticated = useIsAuthenticated()
	const claims = useContext(claimsContext)

	if (!isAuthenticated || !claims) {
		return <>{children}</>
	}

	const isOrgUser = claims.userType === "org"
	const accessToken = useAccessToken()
	const fetch = () => fetchSetup(accessToken)
	const [setupState, fetchSetup] = useAsyncResultIdle(getOrgBankingSetup)

	const shouldBlock = isAuthenticated && isOrgUser && isSandboxOrLocalEnv()

	// Automatically trigger fetching org banking setup
	// when the user is authenticated, an org user, and in sandbox/local environment.
	// Only fetch if setupState is still idle (has not started yet).
	useEffect(() => {
		if (shouldBlock && setupState.isIdle()) {
			fetchSetup(accessToken)
		}
	}, [shouldBlock, setupState, accessToken])

	if (!shouldBlock) {
		return (
			<orgBankingSetupContext.Provider value={{setup: undefined, fetch}}>{children}</orgBankingSetupContext.Provider>
		)
	}

	return setupState.match(
		() => <Loading />,
		() => <Loading />,
		(response) => {
			const setup = response.data.attributes?.orgBankingSetup
			return (
				<orgBankingSetupContext.Provider value={{setup, fetch}}>
					{setup === "NotSet" ? <ChooseBuildPath /> : children}
				</orgBankingSetupContext.Provider>
			)
		},
		(err) => <Err err={err} />
	)
}

export function useOrgBankingSetup() {
	const context = useContext(orgBankingSetupContext)
	assertIsDefined(context)
	return context
}
